import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Person as PersonIcon,
  Group as GroupIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import sharingApi from '../../services/sharingApi';

interface SharedWithDisplayProps {
  tabId: string;
  tabName: string;
}

interface SharedUser {
  id: number;
  email: string;
  username: string;
  shareType: 'email';
}

interface SharedTeam {
  id: number;
  name: string;
  memberCount: number;
  shareType: 'team';
}

interface SharedWithData {
  users: SharedUser[];
  teams: SharedTeam[];
  totalSharedWith: number;
}

const SharedWithDisplay: React.FC<SharedWithDisplayProps> = ({ tabId, tabName }) => {
  const [sharedData, setSharedData] = useState<SharedWithData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [userToRemove, setUserToRemove] = useState<SharedUser | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  useEffect(() => {
    fetchSharedWith();
  }, [tabId]);

  const fetchSharedWith = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await sharingApi.getTabSharedWith(tabId);
      
      if (response.success) {
        setSharedData(response.data);
      } else {
        throw new Error(response.message || 'Failed to load sharing information');
      }
    } catch (error: any) {
      console.error('Error fetching shared with data:', error);
      setError(error.message || 'Failed to load sharing information');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveUser = (user: SharedUser) => {
    setUserToRemove(user);
    setRemoveDialogOpen(true);
  };

  const confirmRemoveUser = async () => {
    if (!userToRemove) return;

    try {
      setIsRemoving(true);
      const response = await sharingApi.removeUserFromTabSharing(tabId, userToRemove.email);
      
      if (response.success) {
        // Refresh the shared with data
        await fetchSharedWith();
        setRemoveDialogOpen(false);
        setUserToRemove(null);
      } else {
        throw new Error(response.message || 'Failed to remove user from sharing');
      }
    } catch (error: any) {
      console.error('Error removing user from sharing:', error);
      setError(error.message || 'Failed to remove user from sharing');
    } finally {
      setIsRemoving(false);
    }
  };

  const cancelRemoveUser = () => {
    setRemoveDialogOpen(false);
    setUserToRemove(null);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading sharing information...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!sharedData || sharedData.totalSharedWith === 0) {
    return (
      <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
          This tab hasn't been shared with anyone yet
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <VisibilityIcon color="primary" />
        Shared With
        <Chip 
          label={sharedData.totalSharedWith} 
          size="small" 
          color="primary" 
          sx={{ ml: 1 }}
        />
      </Typography>

      <Paper sx={{ backgroundColor: 'background.default' }}>
        <List sx={{ py: 0 }}>
          {/* Shared with individuals */}
          {sharedData.users.map((user, index) => (
            <React.Fragment key={`user-${user.id}`}>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={user.email}
                  secondary={user.username !== user.email ? user.username : undefined}
                />
                <Chip 
                  label="Individual" 
                  size="small" 
                  variant="outlined"
                  color="primary"
                  sx={{ mr: 1 }}
                />
                <IconButton
                  onClick={() => handleRemoveUser(user)}
                  color="error"
                  size="small"
                  title={`Remove ${user.email} from sharing`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItem>
              {(index < sharedData.users.length - 1 || sharedData.teams.length > 0) && (
                <Divider variant="inset" component="li" />
              )}
            </React.Fragment>
          ))}

          {/* Shared with teams */}
          {sharedData.teams.map((team, index) => (
            <React.Fragment key={`team-${team.id}`}>
              <ListItem>
                <ListItemIcon>
                  <GroupIcon color="secondary" />
                </ListItemIcon>
                <ListItemText
                  primary={team.name}
                  secondary={`${team.memberCount} member${team.memberCount !== 1 ? 's' : ''}`}
                />
                <Chip 
                  label="Team" 
                  size="small" 
                  variant="outlined"
                  color="secondary"
                />
              </ListItem>
              {index < sharedData.teams.length - 1 && (
                <Divider variant="inset" component="li" />
              )}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        This tab "{tabName}" is currently shared with {sharedData.totalSharedWith} 
        {sharedData.totalSharedWith === 1 ? ' recipient' : ' recipients'}.
        {sharedData.users.length > 0 && sharedData.teams.length > 0 && 
          ` Including ${sharedData.users.length} individual${sharedData.users.length !== 1 ? 's' : ''} and ${sharedData.teams.length} team${sharedData.teams.length !== 1 ? 's' : ''}.`
        }
      </Typography>

      {/* Remove User Confirmation Dialog */}
      <Dialog open={removeDialogOpen} onClose={cancelRemoveUser}>
        <DialogTitle>Remove User from Sharing</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove <strong>{userToRemove?.email}</strong> from accessing the tab "{tabName}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action will immediately remove their access to this tab and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelRemoveUser} disabled={isRemoving}>
            Cancel
          </Button>
          <Button 
            onClick={confirmRemoveUser} 
            color="error" 
            variant="contained"
            disabled={isRemoving}
            startIcon={isRemoving ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isRemoving ? 'Removing...' : 'Remove User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SharedWithDisplay;